/**
 * Module Partage — spec 2026-08-14.
 * Eteint tant que VITE_FF_PARTAGE ne vaut pas "true".
 */
import { ROLES_PARTAGE } from "@geceram/shared";
import { lazy } from "react";
import type { ModuleDef } from "../../core/module-contract";
import { lireJson } from "./api";

const mod: ModuleDef = {
  id: "partage",
  label: "Partage",
  icon: "i-lock",
  navGroup: "operations",
  navOrder: 45,
  // rolesRequis est un TABLEAU STATIQUE (ModuleDef ne peut pas appeler un
  // predicat) : on reutilise ROLES_PARTAGE par reference plutot que de le
  // recopier, pour respecter la §17.5 (jamais deux listes jumelles
  // serveur/front). Le jour ou le controle d'acces devient plus riche qu'une
  // simple liste de roles (ex. depend du lieu, du niveau de divulgation...),
  // c'est le contrat ModuleDef.rolesRequis lui-meme qu'il faudra faire
  // evoluer vers un predicat — pas une nouvelle liste a recopier ici.
  rolesRequis: ROLES_PARTAGE,
  routes: [
    // LE module d'app COURSES, entier : sa page, ses onglets, sa recherche, son
    // bouton « Nouveau cas », son panneau de conversation. Ce ne sont pas des
    // copies inspirées, ce sont les mêmes fichiers ; seul le montage diffère,
    // et c'est exactement ce que le cahier des charges promettait.
    { path: "/partage", component: lazy(() => import("./hote/monter-crm")) },
    // Un dossier ouvert dans l'espace du cas a SA propre adresse (lot Retour,
    // 17/10), comme le `/case/<id>` de Smilecloud : le bouton Retour rend la
    // liste. Même montage : l'hôte lit l'identifiant dans le chemin. APRÈS
    // `/partage`, qui reste `routes[0]`, l'entrée du menu.
    { path: "/partage/cas/:id", component: lazy(() => import("./hote/monter-crm")) },
    // `/partage/lieu` (l'écran du 16/08) a été SUPPRIMÉ au lot 1 de la refonte
    // (30/08) : plus personne ne l'ouvrait, et sa seule fonction restante — le
    // rapprochement d'un cas orphelin vers une fiche patient — revient au
    // lot 2, dans la bannette « À classer » de la page principale.
    // La file des travaux qu aucun dossier ne peut accueillir. Elle est
    // ouverte par la tuile du tableau de bord et par le rappel quotidien :
    // sans cette route, les deux menaient a une page introuvable.
    { path: "/partage/parking", component: lazy(() => import("./routes/Parking")) },
    /*
     * L'ADMINISTRATION DES COMPTES PRATICIENS — etape E9.
     *
     * Montee avec les autres routes du module, donc derriere la session et
     * `rolesRequis`. Le filtre qui compte n'est PAS celui-ci : le serveur
     * refuse tout appelant qui n'est pas administrateur (`canAdministrerComptes`).
     * Cette route n'est qu'un chemin ; un role de lecture qui l'atteindrait
     * verrait un ecran qui ne charge rien, jamais un compte.
     */
    {
      path: "/partage/comptes",
      component: lazy(() =>
        import("./components/ComptesPraticiens").then((m) => ({ default: m.ComptesPraticiens })),
      ),
    },
    /**
     * LA CORBEILLE — étape É19, la dernière pièce du module.
     *
     * ⚠ MÊME REMARQUE QUE POUR LES COMPTES, ET ELLE COMPTE DAVANTAGE ICI : ce
     * chemin n'est pas un contrôle. Le serveur refuse tout appelant qui n'est
     * pas administrateur, et c'est LUI la barrière. Un rôle de lecture qui
     * atteindrait cette adresse verrait le refus s'afficher, jamais un dossier.
     *
     * ⚠ C'EST L'ÉCRAN QUI PORTE LE SEUL GESTE IRRÉVERSIBLE DU MODULE. Il n'est
     * volontairement pas dans la navigation : on ne tombe pas sur la corbeille
     * en se promenant, on y va parce qu'on cherche quelque chose.
     */
    {
      path: "/partage/corbeille",
      component: lazy(() =>
        import("./components/Corbeille").then((m) => ({ default: m.Corbeille })),
      ),
    },
  ],
  /**
   * L'ÉCRAN QU'UN CABINET OUVRE SANS COMPTE — étape É8.
   *
   * ⚠ IL NE PEUT PAS VIVRE DANS `routes` : celles-ci sont filtrées par
   * `rolesRequis` et montées derrière la session. Un cabinet n'a pas de
   * compte — c'est l'objet même du lien. Monté là, il aurait affiché un
   * formulaire de connexion sur un lien que le laboratoire venait d'envoyer,
   * et personne ne l'aurait su avant le premier cabinet.
   *
   * ⚠ LE JETON EST DANS LE FRAGMENT (`/d#<jeton>`), JAMAIS DANS LE CHEMIN.
   * Un fragment n'est pas envoyé au serveur : il n'apparaît ni dans les
   * journaux nginx, ni dans ceux d'un proxy. La forme historique
   * `/partage/public/:token` reste servie pour les liens déjà distribués —
   * `hote/jeton-public.ts` la réécrit aussitôt en forme fragment.
   */
  routesPubliques: [
    { path: "/d", component: lazy(() => import("./pages/PartagePublic")) },
    /*
     * L'ESPACE DU PRATICIEN — etape E9.
     *
     * ⚠ IL EST ICI POUR CASSER UNE BOUCLE. Le callback Google renvoie un
     * dentiste reconnu vers `/mes-dossiers`. Tant que cette adresse tombait
     * dans l'arbre authentifie, il y voyait l'ecran de connexion — il n'a pas
     * de session d'EMPLOYE et n'en aura jamais. Il cliquait, etait reconnu,
     * revenait ici, revoyait l'ecran de connexion : une boucle sans fin, sans
     * le moindre message.
     *
     * « Publique » ne veut PAS dire sans controle : c'est le cookie de
     * praticien, relu en base a chaque requete, qui decide de ce que la page
     * recoit. Elle est publique du point de vue de la session des EMPLOYES.
     */
    { path: "/mes-dossiers", component: lazy(() => import("./pages/MesDossiers")) },
    { path: "/partage/public/:token", component: lazy(() => import("./pages/PartagePublic")) },
  ],
  /**
   * LA PASTILLE DU MENU (lot 4). Le serveur compte les DOSSIERS à non-lus —
   * le prédicat exact du bandeau de la page (§17.5 : le nombre du menu est
   * celui qu'on retrouve en arrivant). `null` sur zéro : pas de pastille.
   */
  badge: {
    queryKey: ["partage", "badge", "non-lus"],
    queryFn: async () => {
      // Par `api.ts` comme tout le module (règle D1 — aucune frontière réseau
      // hors adapters/api) ; NavBadge avale l'échec en « pas de pastille ».
      const { n } = await lireJson<{ n: number }>("/api/v1/partage/non-lus");
      return n > 0 ? n : null;
    },
    refetchIntervalMs: 60_000,
  },
  featureFlag: "VITE_FF_PARTAGE",
};

export default mod;
